import LoggingService from 'client/util/LoggingService';
import globalRouteService from 'client/AdminPortal/router/globalRouteService';

/**
 * This Guard governs which Router (Legacy or Vue) handles a given transition
 *
 * Because of the order they were registered Vue Router's event handler will always trigger first.
 * When a Route is configured in Vue Router it's matched result will contain the route config.
 *
 * When allowing Backbone to handle a route transition we return without calling next. This
 * prevents Vue Router from handling the route at all (either blocking or resolving it) so
 * that the Backbone router can be allow to block or resolve the transition.
 *
 * It's important that these guards are synchronous so that we can know at any given point which
 * router is handling a transition.
 */

// eslint-disable-next-line arrow-body-style
export const createDualRouterSwitchGuard = (_globalRouteService = globalRouteService) => {
  return function dualRouterSwitchGuard(to, from, next) {
    const {
      isVueHandlingRoute, passToBackbone, passToVue,
    } = _globalRouteService;

    const fromVueRoute = from.matched.length > 0;
    const toVueRoute = to.matched.length > 0;
    const fromBackboneRoute = from.matched.length === 0;
    const toBackboneRoute = to.matched.length === 0;
    const fromBasePath = from.path === '/';

    LoggingService.debug('dualRouterSwitchGuard: ', { toVueRoute, fromVueRoute, fromBasePath });

    if (fromBasePath) {
      if (toVueRoute) {
        passToVue();
        next();
        return;
      }
      passToBackbone();
      next();
      return;
    }

    if (fromVueRoute && toVueRoute) {
      passToVue();
      next();
      return;
    }

    if (fromBackboneRoute && toBackboneRoute) {
      passToBackbone();
      return;
    }

    if (fromVueRoute && toBackboneRoute) {
      // Note: Component beforeRouteLeave guards will have already triggered so we can just continue
      passToBackbone();
      next();
      return;
    }

    if (fromBackboneRoute && toVueRoute) {
      /**
       * We want to allow the legacy router to handle navigation guards so we don't continue
       * until it has passed control back to Vue Router
       */
      if (isVueHandlingRoute() === false) {
        passToBackbone();
        return;
      }

      // Vue is now handling the route transition so we continue as normal
      next();
    }
  };
};

export default createDualRouterSwitchGuard();
